import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";

import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import StatCard from "components/StatCard";
import { listingInquiriesRepo } from "data/repositories/listingInquiriesRepo";
import { provincesRepo } from "data/repositories/provincesRepo";

const KIND_LABEL = {
  tour: "Tur",
  business: "İşletme",
  guide: "Rehber",
  hotel: "Otel",
  facility: "Tesis",
  region: "Bölge",
};

const CHANNEL_LABEL = {
  message: "Mesaj",
  email: "E-posta",
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("tr-TR");
}

function DetailRow({ label, children }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children || "—"}</dd>
    </>
  );
}

export default function AdminListingInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const provinceNameById = useMemo(() => {
    const map = new Map();
    for (const province of provincesRepo.list()) map.set(province.id, province.name);
    return map;
  }, []);
  const provinceName = (id) => provinceNameById.get(id) || id || "—";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setItems(await listingInquiriesRepo.list());
    } catch (loadError) {
      setError(loadError?.message || "İlan mesajları yüklenemedi.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (kindFilter && item.kind !== kindFilter) return false;
      if (!query) return true;
      return [
        item.listingTitle,
        item.fromName,
        item.fromEmail,
        item.fromPhone,
        item.subject,
        item.message,
        item.listingOwnerEmail,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [items, search, kindFilter]);

  const selected = filtered.find((item) => item.id === selectedId) || filtered[0] || null;

  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
    if (!selected && selectedId) setSelectedId("");
  }, [selected, selectedId]);

  const kindCounts = useMemo(() => {
    const counts = {};
    for (const item of items) counts[item.kind] = (counts[item.kind] || 0) + 1;
    return counts;
  }, [items]);

  const filters = [
    {
      key: "kind",
      label: "Tür",
      value: kindFilter,
      onChange: setKindFilter,
      options: [
        { value: "", label: "Tüm türler" },
        ...Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
      ],
    },
  ];

  return (
    <>
      <AdminPageHeader
        title="İlan mesajları"
        description="Tur, otel, rehber ve tesis ilanlarından gelen mesaj / e-posta başvuruları. Her kayıt KADEKON / Destinasyon 360 raporuna düşer."
        primaryAction={load}
        primaryLabel="Yenile"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Toplam mesaj" value={items.length} icon="fas fa-envelope" color="primary" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Tur" value={kindCounts.tour || 0} icon="fas fa-map" color="warning" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Otel / Tesis" value={(kindCounts.hotel || 0) + (kindCounts.facility || 0)} icon="fas fa-bed" color="info" />
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <StatCard title="Rehber / diğer" value={(kindCounts.guide || 0) + (kindCounts.business || 0) + (kindCounts.region || 0)} icon="fas fa-user" color="success" />
          </Col>
        </Row>

        <Row>
          <Col xl="7" className="mb-4">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-3">Gelen başvurular</h3>
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="İlan, gönderen, e-posta veya mesaj ara"
                  filters={filters}
                  sortValue="created-desc"
                  onSortChange={() => {}}
                  sortOptions={[{ value: "created-desc", label: "En yeni" }]}
                  onReset={() => {
                    setSearch("");
                    setKindFilter("");
                  }}
                  resultCount={filtered.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody>
                <AdminStateBlock
                  error={error}
                  onDismissError={() => setError("")}
                  loading={loading}
                  isEmpty={!filtered.length}
                  emptyTitle="Mesaj yok"
                  emptyDescription="Henüz ilan üzerinden mesaj / e-posta başvurusu gelmemiş."
                >
                  <Table responsive hover className="align-items-center mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>İlan</th>
                        <th>Gönderen</th>
                        <th>Kanal</th>
                        <th>Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((item) => (
                        <tr
                          key={item.id}
                          onClick={() => setSelectedId(item.id)}
                          style={{
                            cursor: "pointer",
                            background: selected?.id === item.id ? "rgba(94, 114, 228, 0.08)" : undefined,
                          }}
                        >
                          <td>
                            <div className="font-weight-bold">{item.listingTitle || "—"}</div>
                            <small className="text-muted d-block">
                              {KIND_LABEL[item.kind] || item.kind}
                              {item.provinceId ? ` · ${provinceName(item.provinceId)}` : ""}
                            </small>
                          </td>
                          <td>
                            <div>{item.fromName || "—"}</div>
                            <small className="text-muted d-block">{item.fromEmail}</small>
                          </td>
                          <td>
                            <Badge color={item.channel === "email" ? "info" : "secondary"} pill>
                              {CHANNEL_LABEL[item.channel] || item.channel}
                            </Badge>
                          </td>
                          <td>{formatDateTime(item.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>

          <Col xl="5" className="mb-4">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">Mesaj detayı</h3>
                <small className="text-muted">Seçilen başvurunun içeriği ve iletişim bilgileri.</small>
              </CardHeader>
              <CardBody>
                {!selected ? (
                  <p className="text-muted mb-0">İncelemek için soldan bir mesaj seçin.</p>
                ) : (
                  <dl className="mb-0">
                    <DetailRow label="İlan">{selected.listingTitle}</DetailRow>
                    <DetailRow label="Tür">{KIND_LABEL[selected.kind] || selected.kind}</DetailRow>
                    <DetailRow label="Şehir">{provinceName(selected.provinceId)}</DetailRow>
                    <DetailRow label="Kanal">{CHANNEL_LABEL[selected.channel] || selected.channel}</DetailRow>
                    <DetailRow label="Konu">{selected.subject}</DetailRow>
                    <DetailRow label="Mesaj">
                      {selected.message ? <span style={{ whiteSpace: "pre-wrap" }}>{selected.message}</span> : null}
                    </DetailRow>
                    <DetailRow label="Gönderen">{selected.fromName}</DetailRow>
                    <DetailRow label="E-posta">
                      {selected.fromEmail ? <a href={`mailto:${selected.fromEmail}`}>{selected.fromEmail}</a> : null}
                    </DetailRow>
                    <DetailRow label="Telefon">
                      {selected.fromPhone ? (
                        <a href={`tel:${selected.fromPhone.replace(/\s/g, "")}`}>{selected.fromPhone}</a>
                      ) : null}
                    </DetailRow>
                    <DetailRow label="İlan sahibi e-posta">{selected.listingOwnerEmail}</DetailRow>
                    <DetailRow label="İlan sahibi telefon">{selected.listingOwnerPhone}</DetailRow>
                    <DetailRow label="Kaynak">{selected.source || "—"}</DetailRow>
                    <DetailRow label="Tarih">{formatDateTime(selected.createdAt)}</DetailRow>
                    <DetailRow label="Rapor">
                      <Badge color="success" pill>
                        reportToAdmin
                      </Badge>
                    </DetailRow>
                  </dl>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
